import { type RefObject, useCallback } from 'react';

import { useTelemetry } from '@order/Telemetry';
import { getFetchOptions } from '@order/utils';

import { useHandleGiftCardResponse } from '../useHandleGiftCardResponse';
import {
  type RedeemGiftCardMutationVariables,
  useRedeemGiftCardMutation,
} from './graphql/RedeemGiftCard.generated';

// ────────────────────────────────────────────────────────────────────────────────

export const useRedeemGiftCard = (props: UseRedeemGiftCardProps) => {
  const { generateAwsWafTokenRef } = props;

  const [, redeemGiftCardMutation] = useRedeemGiftCardMutation();

  const { track } = useTelemetry();
  const { handleGiftCardResponse } = useHandleGiftCardResponse();

  const redeemGiftCard = useCallback(
    async (variables: RedeemGiftCardMutationVariables) => {
      track('gift-card.redemption.submit', { source: 'account' });

      const awsWafToken = await generateAwsWafTokenRef.current();

      const { data, error } = await redeemGiftCardMutation(variables, {
        requestPolicy: 'network-only',
        ...(awsWafToken
          ? {
              fetchOptions: getFetchOptions({
                extraHeaders: {
                  'x-aws-waf-token': awsWafToken,
                },
              }),
            }
          : {}),
      });

      const response = data?.redeemGiftCard;

      return await handleGiftCardResponse({ response, error, source: 'account' });
    },
    [redeemGiftCardMutation, generateAwsWafTokenRef, track, handleGiftCardResponse],
  );

  return { redeemGiftCard };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseRedeemGiftCardProps = {
  generateAwsWafTokenRef: RefObject<() => Promise<string>>;
};
